/**
 * User honor journey page.
 *
 * Shows the logged-in user's achievement milestones in a timeline-style list.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { honorAPI } from '../api/honor';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedChip, ThemedMetric, ThemedPanel, ThemedSubmitButton } from '../components/ui/ThemedSurface';

interface Milestone {
  id: number;
  milestone: string;
  title: string;
  description: string | null;
  value: string | null;
  badge: string | null;
  create_time: string;
}

const MILESTONE_LABEL_KEYS: Record<string, string> = {
  level_up: 'honor.milestones.levelUp',
  donation_tier: 'honor.milestones.donationTier',
  trade_count: 'honor.milestones.tradeCount',
  first_recharge: 'honor.milestones.firstRecharge',
  first_trade: 'honor.milestones.firstTrade',
  referral_count: 'honor.milestones.referralCount',
};

const MILESTONE_TONES: Record<string, string> = {
  level_up: C.gold,
  donation_tier: C.buy,
  trade_count: C.blue,
  first_recharge: C.chartColor,
  first_trade: C.gold,
  referral_count: C.buy,
};

function formatHonorDate(value: string, language: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString(language.startsWith('zh') ? 'zh-CN' : 'en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

function MilestoneIcon({ badge, color }: { badge: string | null; color: string }) {
  if (badge) {
    return (
      <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border text-xl" style={{ borderColor: `${color}66`, background: `${color}14`, boxShadow: `0 0 18px ${color}20` }}>
        {badge}
      </span>
    );
  }

  return (
    <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: `${color}66`, background: `${color}14`, color, boxShadow: `0 0 18px ${color}20` }}>
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.4 6.8 19.1l1-5.8-4.3-4.1 5.9-.9L12 3Z" />
      </svg>
    </span>
  );
}

function HonorHero({ total, page, totalPages }: { total: number; page: number; totalPages: number }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xl font-semibold leading-none" style={{ color: C.text }}>{t('honor.title')}</div>
            <div className="mt-2 text-xs leading-relaxed" style={{ color: C.muted }}>{t('honor.subtitle')}</div>
          </div>
          <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: `${C.gold}66`, background: C.chipBg, color: C.gold, boxShadow: `0 0 20px ${C.gold}18` }}>
            <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 17v4" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5H3v2a4 4 0 0 0 4 4" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 5h2v2a4 4 0 0 1-4 4" />
            </svg>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-0 rounded-xl border px-3 py-3" style={{ borderColor: C.line, background: C.inputBg }}>
          <ThemedMetric label={t('honor.totalAchievements')} value={total} color={C.gold} />
          <div className="relative">
            <div className="absolute bottom-1 left-0 top-1 w-px" style={{ background: `linear-gradient(180deg, transparent 0%, ${C.gold}66 50%, transparent 100%)` }} />
            <div className="pl-4">
              <ThemedMetric label={t('honor.page')} value={`${page} / ${Math.max(totalPages, 1)}`} color={C.text} />
            </div>
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

function HonorCard({ item }: { item: Milestone }) {
  const { t, i18n } = useTranslation();
  const color = MILESTONE_TONES[item.milestone] ?? C.gold;
  const labelKey = MILESTONE_LABEL_KEYS[item.milestone];
  const label = labelKey ? t(labelKey) : item.milestone;

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute bottom-4 left-[37px] top-[59px] w-px" style={{ background: `linear-gradient(180deg, ${color}66 0%, transparent 100%)` }} />
        <div className="flex items-start gap-3">
          <MilestoneIcon badge={item.badge} color={color} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <ThemedChip active>{label}</ThemedChip>
              <time className="shrink-0 text-[10px]" style={{ color: C.muted }}>{formatHonorDate(item.create_time, i18n.language)}</time>
            </div>
            <div className="mt-3 text-base font-semibold leading-snug" style={{ color: C.text }}>{item.title}</div>
            {item.description ? <div className="mt-1 text-sm leading-relaxed" style={{ color: C.muted }}>{item.description}</div> : null}
            {item.value ? (
              <div className="mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs" style={{ borderColor: `${color}55`, background: `${color}12`, color }}>
                <span>{t('honor.value')}</span>
                <span className="font-mono font-semibold">{item.value}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </ThemedPanel>
  );
}

function HonorEmptyState() {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-4">
      <EmptyState
        icon={
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8M12 17v4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 5H3v2a4 4 0 0 0 4 4M19 5h2v2a4 4 0 0 1-4 4" />
          </svg>
        }
        title={t('honor.emptyTitle')}
        description={t('honor.emptyDescription')}
        className="py-8"
      />
    </ThemedPanel>
  );
}

function Pager({ page, totalPages, onPrev, onNext }: { page: number; totalPages: number; onPrev: () => void; onNext: () => void }) {
  const { t } = useTranslation();

  if (totalPages <= 1) return null;

  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2">
      <ThemedSubmitButton disabled={page <= 1} onClick={onPrev} className="h-10">
        {t('honor.prevPage')}
      </ThemedSubmitButton>
      <span className="rounded-full border px-3 py-2 text-xs font-semibold" style={{ borderColor: C.softBorder, background: C.inputBg, color: C.muted }}>
        {page} / {totalPages}
      </span>
      <ThemedSubmitButton disabled={page >= totalPages} onClick={onNext} className="h-10">
        {t('honor.nextPage')}
      </ThemedSubmitButton>
    </div>
  );
}

export function HonorJourney() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const totalPages = Math.ceil(total / limit);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await honorAPI.getRankings({ page, limit });
      setItems((res.rows ?? []) as unknown as Milestone[]);
      setTotal(res.total ?? 0);
    } catch {
      /* keep existing silent failure behavior */
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { void fetch(); }, [fetch]);

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('honor.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '28px' }}>
          <h1 className="sr-only">{t('honor.myJourney')}</h1>
          <HonorHero total={total} page={page} totalPages={totalPages} />

          {loading && items.length === 0 ? (
            <div className="flex flex-col gap-3">
              <LoadingSkeleton variant="card" />
              <LoadingSkeleton variant="card" />
              <LoadingSkeleton variant="card" />
            </div>
          ) : items.length === 0 ? (
            <HonorEmptyState />
          ) : (
            <div className="flex flex-col gap-3">
              {items.map((item) => <HonorCard key={item.id} item={item} />)}
              <Pager
                page={page}
                totalPages={totalPages}
                onPrev={() => setPage((current) => Math.max(1, current - 1))}
                onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
